import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS, getOrderStatusLabel, getDeliveryStatusLabel } from "../../utils/constants";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatLabel = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const formatPhone = (user) => {
  if (!user) return "-";
  const code = user.country_code ? `+${user.country_code}` : "";
  const phone = user.phone || "";
  if (!code && !phone) return "-";
  return `${code} ${phone}`.trim();
};

const extractNoteDetail = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.payout_cr_dr_note && typeof payload.payout_cr_dr_note === "object") {
    return payload.payout_cr_dr_note;
  }
  if (payload._id || payload.cr_dr_note_id) return payload;
  return null;
};

const DetailCard = ({ label, children }) => (
  <div className="col-md-4 mb-3">
    <div className="border rounded-14 p-3 h-100">
      <small className="text-muted d-block mb-1">{label}</small>
      <strong className="text-white text-break">{children}</strong>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="border rounded-14 p-4 mb-4">
    <h5 className="text-white mb-3">{title}</h5>
    {children}
  </div>
);

function PayoutCrDrNoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.getPayoutCrDrNoteDetail}?id=${id}`
      );

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        setNote(extractNoteDetail(response?.data?.data));
      } else {
        setNote(null);
      }
    } catch (error) {
      console.error("Error fetching payout CR/DR note detail:", error);
      setNote(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const order = note?.order || null;
  const restaurant = note?.restaurant || null;
  const driver = note?.driver || null;
  const wallets = Array.isArray(note?.wallets) ? note.wallets : [];
  const userType = String(note?.user_type || "").toLowerCase();
  const isRestaurantUser = userType === "restaurant" || userType === "both";
  const isDriverUser = userType === "driver" || userType === "both";
  const showRestaurantSection =
    isRestaurantUser &&
    Boolean(
      restaurant &&
        (restaurant._id ||
          restaurant.full_name ||
          restaurant.email ||
          restaurant.phone ||
          note?.restaurant_id)
    );
  const showDriverSection =
    isDriverUser &&
    Boolean(
      driver &&
        (driver._id ||
          driver.full_name ||
          driver.email ||
          driver.phone ||
          note?.driver_id)
    );

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">
                Payout Credit &amp; Debit Detail
              </h3>
            </div>

            <div className="mb-3">
              <button
                type="button"
                className="btn btn-default rounded-lg"
                onClick={() => navigate("/payout-management/credit-debit")}
              >
                Back
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">
                    {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                  </span>
                </div>
              </div>
            ) : !note ? (
              <div className="text-center py-4">
                <p>{UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}</p>
              </div>
            ) : (
              <>
                <Section title="Note Details">
                  <div className="row">
                    <DetailCard label="Note ID">
                      {formatValue(note.cr_dr_note_id)}
                    </DetailCard>
                    <DetailCard label="Record ID">{formatValue(note._id)}</DetailCard>
                    <DetailCard label="Note Type">
                      {formatLabel(note.cr_dr_note_type)}
                    </DetailCard>
                    <DetailCard label="User Type">
                      {formatLabel(note.user_type)}
                    </DetailCard>
                    {isRestaurantUser && (
                      <DetailCard label="Restaurant Amount">
                        {formatMoney(note.restaurant_amount)}
                      </DetailCard>
                    )}
                    {isDriverUser && (
                      <DetailCard label="Driver Amount">
                        {formatMoney(note.driver_amount)}
                      </DetailCard>
                    )}
                    <DetailCard label="Sum Of Amount">
                      {formatMoney(note.sum_of_amount)}
                    </DetailCard>
                    {isRestaurantUser && (
                      <>
                        <DetailCard label="Restaurant Settlement Status">
                          {formatValue(note.restaurant_sattlement_status_str)}
                        </DetailCard>
                        <DetailCard label="Restaurant Settlement Code">
                          {formatValue(note.restaurant_sattlement_status)}
                        </DetailCard>
                      </>
                    )}
                    {isDriverUser && (
                      <>
                        <DetailCard label="Driver Settlement Status">
                          {formatValue(note.driver_sattlement_status_str)}
                        </DetailCard>
                        <DetailCard label="Driver Settlement Code">
                          {formatValue(note.driver_sattlement_status)}
                        </DetailCard>
                      </>
                    )}
                    <DetailCard label="Created By">
                      {formatValue(note.created_by)}
                    </DetailCard>
                    <DetailCard label="Updated By">
                      {formatValue(note.updated_by)}
                    </DetailCard>
                    <DetailCard label="Created">
                      {formatDateTime(note.createdAt)}
                    </DetailCard>
                    <DetailCard label="Updated">
                      {formatDateTime(note.updatedAt)}
                    </DetailCard>
                    <div className="col-12 mb-3">
                      <div className="border rounded-14 p-3">
                        <small className="text-muted d-block mb-1">
                          Description
                        </small>
                        <strong className="text-white text-break">
                          {formatValue(note.description)}
                        </strong>
                      </div>
                    </div>
                  </div>
                </Section>

                <Section title="Order Details">
                  <div className="row">
                    <DetailCard label="Order Number">
                      {formatValue(order?.order_number)}
                    </DetailCard>
                    <DetailCard label="Order Status">
                      {order?.order_status !== undefined &&
                      order?.order_status !== null
                        ? getOrderStatusLabel(order.order_status)
                        : "-"}
                    </DetailCard>
                    <DetailCard label="Delivery Status">
                      {order?.delivery_status !== undefined &&
                      order?.delivery_status !== null
                        ? getDeliveryStatusLabel(order.delivery_status)
                        : "-"}
                    </DetailCard>
                  </div>
                </Section>

                {showRestaurantSection && (
                <Section title="Restaurant Details">
                  <div className="row">
                    <DetailCard label="Name">
                      {formatValue(restaurant?.full_name)}
                    </DetailCard>
                    <DetailCard label="Email">
                      {formatValue(restaurant?.email)}
                    </DetailCard>
                    <DetailCard label="Phone">
                      {formatPhone(restaurant)}
                    </DetailCard>
                  </div>
                </Section>
                )}

                {showDriverSection && (
                <Section title="Driver Details">
                  <div className="row">
                    <DetailCard label="Driver ID">
                      {formatValue(note.driver_id || driver?._id)}
                    </DetailCard>
                    <DetailCard label="Name">
                      {formatValue(driver?.full_name)}
                    </DetailCard>
                    <DetailCard label="Email">
                      {formatValue(driver?.email)}
                    </DetailCard>
                    <DetailCard label="Phone">{formatPhone(driver)}</DetailCard>
                  </div>
                </Section>
                )}

                <Section title="Wallet Entries">
                  {wallets.length > 0 ? (
                    wallets.map((wallet, index) => (
                      <div
                        key={wallet._id || index}
                        className={`border rounded-14 p-3 ${
                          index < wallets.length - 1 ? "mb-3" : ""
                        }`}
                      >
                        <h6 className="text-white mb-3">
                          Wallet Entry {index + 1}
                        </h6>
                        <div className="row">
                          <DetailCard label="Wallet Entry ID">
                            {formatValue(wallet._id)}
                          </DetailCard>
                          <DetailCard label="User Type">
                            {formatLabel(wallet.user_type)}
                          </DetailCard>
                          <DetailCard label="User ID">
                            {formatValue(wallet.user_id || wallet.user?._id)}
                          </DetailCard>
                          <DetailCard label="User Name">
                            {formatValue(wallet.user?.full_name)}
                          </DetailCard>
                          <DetailCard label="User Email">
                            {formatValue(wallet.user?.email)}
                          </DetailCard>
                          <DetailCard label="User Phone">
                            {formatPhone(wallet.user)}
                          </DetailCard>
                          <DetailCard label="Order ID">
                            {formatValue(wallet.order_id)}
                          </DetailCard>
                          <DetailCard label="CR/DR Note ID">
                            {formatValue(wallet.cr_dr_note_id)}
                          </DetailCard>
                          <DetailCard label="Wallet Entry Type">
                            {formatLabel(wallet.wallet_entry_type)}
                          </DetailCard>
                          <DetailCard label="Transaction Type">
                            {formatLabel(wallet.transaction_type)}
                          </DetailCard>
                          <DetailCard label="Transaction Amount">
                            {formatMoney(wallet.transaction_amount)}
                          </DetailCard>
                          <DetailCard label="Balance After">
                            {formatMoney(wallet.balance_after)}
                          </DetailCard>
                          <DetailCard label="Settlement Status">
                            {formatValue(wallet.settlement_status)}
                          </DetailCard>
                          <DetailCard label="Wallet Payout ID">
                            {formatValue(wallet.wallet_payout_id)}
                          </DetailCard>
                          <DetailCard label="Transaction Date">
                            {formatDateTime(wallet.transaction_date)}
                          </DetailCard>
                          <DetailCard label="Created">
                            {formatDateTime(wallet.createdAt)}
                          </DetailCard>
                          <DetailCard label="Updated">
                            {formatDateTime(wallet.updatedAt)}
                          </DetailCard>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">
                      {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                    </p>
                  )}
                </Section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayoutCrDrNoteDetail;
